import React, {useEffect, useMemo, useState} from 'react'
import {CopyToClipboard} from 'react-copy-to-clipboard'
import {saveAs} from 'file-saver'
import {useLocation, useNavigate} from 'react-router-dom'
import {
  createStudentClearanceRequest,
  downloadGraduateCv,
  fetchGraduateProfile,
  saveGraduateProfile,
  submitGraduateApplication,
  uploadGraduateCv,
  uploadGraduatePhoto,
  GraduateProfile,
  StudentClearanceCreatePayload,
} from '../CustomController'

type GraduateCardNavigationState = {
  studentClearanceReturnTo?: string
  studentClearanceRequest?: StudentClearanceCreatePayload
}

const statusLabelMap: Record<string, string> = {
  Approved: 'Onaylandı',
  Submitted: 'Başvuru Alındı',
  Draft: 'Taslak',
  Rejected: 'Reddedildi',
}

const GraduateCardBox = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const navigationState = location.state as GraduateCardNavigationState | null
  const pendingStudentClearanceRequest = navigationState?.studentClearanceRequest
  const isStudentClearanceContinuation = !!(
    navigationState?.studentClearanceReturnTo && pendingStudentClearanceRequest
  )
  const [profile, setProfile] = useState<GraduateProfile | null>(null)
  const [form, setForm] = useState<any>({
    email: '',
    phone: '',
    isWorking: false,
    employerName: '',
    isJobSeeking: false,
  })
  const [loading, setLoading] = useState<boolean>(true)
  const [saving, setSaving] = useState<boolean>(false)
  const [uploadingCv, setUploadingCv] = useState<boolean>(false)
  const [uploadingPhoto, setUploadingPhoto] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [copied, setCopied] = useState<boolean>(false)
  const [graduateDataConsent, setGraduateDataConsent] = useState(false)

  const loadProfile = () => {
    setLoading(true)
    setError(null)
    fetchGraduateProfile()
      .then((res) => {
        const data = res.data as GraduateProfile
        setProfile(data)
        setForm({
          email: data?.email || pendingStudentClearanceRequest?.lastContactEmail || '',
          phone: data?.phone || pendingStudentClearanceRequest?.lastContactPhone || '',
          isWorking:
            typeof pendingStudentClearanceRequest?.graduateIsWorking === 'boolean'
              ? pendingStudentClearanceRequest.graduateIsWorking
              : !!data?.isWorking,
          employerName:
            pendingStudentClearanceRequest?.graduateEmployerName || data?.employerName || '',
          isJobSeeking:
            typeof pendingStudentClearanceRequest?.graduateIsJobSeeking === 'boolean'
              ? pendingStudentClearanceRequest.graduateIsJobSeeking
              : !!data?.isJobSeeking,
        })
      })
      .catch(() => {
        setError('Mezun bilgileri alınırken bir hata oluştu.')
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadProfile()
  }, [])

  const statusText = useMemo(() => {
    const status = profile?.applicationStatus || 'Draft'
    return statusLabelMap[status] || 'Taslak'
  }, [profile?.applicationStatus])

  const isApproved = profile?.applicationStatus === 'Approved'
  const isSubmitted = profile?.applicationStatus === 'Submitted'
  const isRejected = profile?.applicationStatus === 'Rejected'

  const cardLink = useMemo(() => {
    if (!profile?.cardCode || !isApproved) {
      return null
    }
    if (typeof window !== 'undefined') {
      return `${window.location.origin}/gc/${profile.cardCode}`
    }
    return `/gc/${profile.cardCode}`
  }, [isApproved, profile?.cardCode])

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value =
      field === 'isWorking' || field === 'isJobSeeking' ? event.target.checked : event.target.value
    setForm((prev: any) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleSave = () => {
    setSaving(true)
    setSuccess(null)
    setError(null)
    saveGraduateProfile({
      email: form.email,
      phone: form.phone,
      isWorking: form.isWorking,
      employerName: form.employerName,
      isJobSeeking: form.isJobSeeking,
    })
      .then(() => {
        setSuccess('Bilgileriniz kaydedildi.')
        loadProfile()
      })
      .catch(() => {
        setError('Bilgiler kaydedilirken bir hata oluştu.')
      })
      .finally(() => setSaving(false))
  }

  const handleSubmitApplication = () => {
    if (isStudentClearanceContinuation && pendingStudentClearanceRequest) {
      const email = String(form.email || '').trim()
      const phone = String(form.phone || '').trim()
      const employerName = String(form.employerName || '').trim()

      if (!email || !phone) {
        setError('Mezun kartı başvurusu için güncel e-posta ve telefon zorunludur.')
        return
      }
      if (!/^[^\s@]+@[^\s@]+$/.test(email)) {
        setError('Geçerli bir e-posta adresi giriniz.')
        return
      }
      if (form.isWorking && !employerName) {
        setError('Çalışıyorsanız çalıştığınız kurum bilgisini giriniz.')
        return
      }
      if (!graduateDataConsent) {
        setError('Mezun kartı ve ilişik kesme işlemleri için açık onay zorunludur.')
        return
      }

      setSaving(true)
      setSuccess(null)
      setError(null)

      createStudentClearanceRequest({
        ...pendingStudentClearanceRequest,
        lastContactEmail: email,
        lastContactPhone: phone,
        wantsVirtualGraduateCard: true,
        graduateIsWorking: !!form.isWorking,
        graduateEmployerName: form.isWorking ? employerName : null,
        graduateIsJobSeeking: !!form.isJobSeeking,
        graduateDataConsent: true,
      })
        .then(() => {
          navigate(navigationState!.studentClearanceReturnTo!, {
            replace: true,
            state: {
              studentClearanceFeedback: {
                type: 'success',
                message:
                  'Mezun kartı başvurunuz tamamlandı ve ilişik kesme başvurunuz oluşturuldu.',
              },
            },
          })
        })
        .catch((requestError) => {
          const message =
            typeof requestError?.response?.data === 'string'
              ? requestError.response.data
              : 'Mezun kartı veya ilişik kesme başvurusu tamamlanamadı.'
          setError(message)
        })
        .finally(() => setSaving(false))
      return
    }

    setSaving(true)
    setSuccess(null)
    setError(null)
    submitGraduateApplication()
      .then(() => {
        setSuccess('Başvurunuz alındı.')
        loadProfile()
      })
      .catch(() => {
        setError('Önce bilgilerinizi kaydetmelisiniz.')
      })
      .finally(() => setSaving(false))
  }

  const handleCvUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) {
      return
    }
    if (file.type !== 'application/pdf') {
      setError('CV yalnızca PDF formatında yüklenebilir.')
      event.target.value = ''
      return
    }
    const formData = new FormData()
    formData.append('file', file)
    setUploadingCv(true)
    setError(null)
    setSuccess(null)
    uploadGraduateCv(formData)
      .then(() => {
        setSuccess('CV dosyanız güncellendi.')
        loadProfile()
      })
      .catch(() => {
        setError('CV yüklenirken bir hata oluştu.')
      })
      .finally(() => {
        setUploadingCv(false)
        event.target.value = ''
      })
  }

  const handleCvDownload = () => {
    downloadGraduateCv()
      .then((res) => {
        const fileName = profile?.cvFileName || 'mezun-cv.pdf'
        saveAs(res.data, fileName)
      })
      .catch(() => {
        setError('CV indirilirken bir hata oluştu.')
      })
  }

  const handlePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) {
      return
    }
    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      setError('Fotoğraf yalnızca PNG veya JPEG formatında olmalıdır.')
      event.target.value = ''
      return
    }
    const formData = new FormData()
    formData.append('photo', file)
    setUploadingPhoto(true)
    setError(null)
    setSuccess(null)
    uploadGraduatePhoto(formData)
      .then(() => {
        setSuccess('Fotoğrafınız güncellendi.')
        loadProfile()
      })
      .catch(() => {
        setError('Fotoğraf yüklenirken bir hata oluştu.')
      })
      .finally(() => {
        setUploadingPhoto(false)
        event.target.value = ''
      })
  }

  if (loading) {
    return (
      <div className='card card-xl-stretch mb-5'>
        <div className='card-body py-10 text-center'>Yükleniyor...</div>
      </div>
    )
  }

  if (!profile?.isGraduate && !isStudentClearanceContinuation) {
    return null
  }

  return (
    <div className='card card-xl-stretch mb-5'>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3'>Mezun Kartım</span>
          <span className='text-muted mt-2 fw-semibold fs-6'>
            Mezun kartı başvurunuzu güncelleyebilirsiniz.
          </span>
        </h3>
      </div>
      <div className='card-body pt-0'>
        {isStudentClearanceContinuation && (
          <div className='alert alert-info'>
            Mezuniyet nedeniyle ilişik kesme işleminden önce mezun kartı formunu tamamlayınız. Bu
            form gönderildiğinde ilişik kesme başvurunuz otomatik oluşturulacaktır.
          </div>
        )}
        {error && <div className='alert alert-danger'>{error}</div>}
        {success && <div className='alert alert-success'>{success}</div>}
        <div className='row mb-5'>
          <div className='col-lg-4 text-center'>
            <div className='mb-3'>
              {profile?.photoUrl ? (
                <img
                  src={profile.photoUrl}
                  alt='Mezun Fotoğrafı'
                  className='img-fluid rounded'
                  style={{maxHeight: '220px'}}
                />
              ) : (
                <div className='bg-light rounded p-10 fw-semibold'>Fotoğraf bulunmuyor</div>
              )}
            </div>
            <div className='d-grid gap-2'>
              <label className='btn btn-sm btn-light-primary'>
                {uploadingPhoto ? 'Yükleniyor...' : 'Fotoğraf Güncelle'}
                <input
                  type='file'
                  accept='image/png,image/jpeg'
                  className='d-none'
                  onChange={handlePhotoUpload}
                  disabled={uploadingPhoto}
                />
              </label>
            </div>
          </div>
          <div className='col-lg-8'>
            <div className='row g-5'>
              <div className='col-md-6'>
                <label className='form-label'>Güncel E-posta</label>
                <input
                  type='email'
                  className='form-control'
                  value={form.email}
                  onChange={handleInputChange('email')}
                  placeholder='ornek@eposta.com'
                />
              </div>
              <div className='col-md-6'>
                <label className='form-label'>Telefon</label>
                <input
                  type='text'
                  className='form-control'
                  value={form.phone}
                  onChange={handleInputChange('phone')}
                  placeholder='+90 5XX XXX XX XX'
                />
              </div>
              <div className='col-md-6'>
                <div className='form-check mt-8'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    id='isWorking'
                    checked={!!form.isWorking}
                    onChange={handleInputChange('isWorking')}
                  />
                  <label className='form-check-label' htmlFor='isWorking'>
                    Çalışıyorum
                  </label>
                </div>
              </div>
              <div className='col-md-6'>
                <div className='form-check mt-8'>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    id='isJobSeeking'
                    checked={!!form.isJobSeeking}
                    onChange={handleInputChange('isJobSeeking')}
                  />
                  <label className='form-check-label' htmlFor='isJobSeeking'>
                    İş arıyorum
                  </label>
                </div>
              </div>
              <div className='col-md-12'>
                <label className='form-label'>Çalıştığınız Kurum</label>
                <input
                  type='text'
                  className='form-control'
                  value={form.employerName}
                  onChange={handleInputChange('employerName')}
                  placeholder='Kurum adı'
                  disabled={!form.isWorking}
                />
              </div>
            </div>
            {isStudentClearanceContinuation && (
              <div className='form-check mt-5'>
                <input
                  id='graduate-clearance-consent'
                  className='form-check-input'
                  type='checkbox'
                  checked={graduateDataConsent}
                  onChange={(event) => setGraduateDataConsent(event.target.checked)}
                  disabled={saving}
                />
                <label className='form-check-label' htmlFor='graduate-clearance-consent'>
                  İletişim ve çalışma bilgilerimin mezun profiline aktarılmasını, mezun kartı
                  başvurumun gönderilmesini ve ardından ilişik kesme başvurumun başlatılmasını
                  onaylıyorum. *
                </label>
              </div>
            )}
            <div className='mt-5 d-flex flex-wrap gap-3'>
              {!isStudentClearanceContinuation && (
                <button className='btn btn-primary' onClick={handleSave} disabled={saving}>
                  {saving ? 'Kaydediliyor...' : 'Bilgileri Kaydet'}
                </button>
              )}
              <button
                className='btn btn-success'
                onClick={handleSubmitApplication}
                disabled={
                  saving || (!isStudentClearanceContinuation && (isApproved || isSubmitted))
                }
              >
                {saving
                  ? 'Gönderiliyor...'
                  : isStudentClearanceContinuation
                  ? 'Mezun Kartını Tamamla ve İlişik Kesmeyi Başlat'
                  : 'Başvuruyu Gönder'}
              </button>
            </div>
          </div>
        </div>
        <div className='separator my-5'></div>
        <div className='row align-items-center g-5'>
          <div className='col-lg-6'>
            <h5 className='fw-bold'>CV Yükleme</h5>
            <div className='d-flex flex-wrap align-items-center gap-3 mt-3'>
              <label className='btn btn-light-primary btn-sm mb-0'>
                {uploadingCv ? 'Yükleniyor...' : 'CV Yükle'}
                <input
                  type='file'
                  accept='application/pdf'
                  className='d-none'
                  onChange={handleCvUpload}
                  disabled={uploadingCv}
                />
              </label>
              {profile?.cvFileName && (
                <button
                  className='btn btn-outline btn-outline-primary btn-sm'
                  onClick={handleCvDownload}
                >
                  CV'yi İndir
                </button>
              )}
              {profile?.cvFileName && (
                <span className='text-muted small'>{profile.cvFileName}</span>
              )}
            </div>
          </div>
          <div className='col-lg-6'>
            <div className='border rounded p-4 bg-light'>
              <div className='d-flex justify-content-between align-items-center mb-2'>
                <span className='fw-semibold'>Başvuru Durumu</span>
                <span className='badge badge-light-primary fs-6'>{statusText}</span>
              </div>
              {profile?.submittedAt && (
                <div className='text-muted small'>
                  Başvuru Tarihi: {new Date(profile.submittedAt).toLocaleString()}
                </div>
              )}
              {profile?.approvedAt && (
                <div className='text-muted small'>
                  Onay Tarihi: {new Date(profile.approvedAt).toLocaleString()}
                </div>
              )}
              {profile?.rejectedAt && (
                <div className='text-muted small'>
                  Red Tarihi: {new Date(profile.rejectedAt).toLocaleString()}
                </div>
              )}
              {cardLink && (
                <div className='mt-3'>
                  <div className='fw-semibold mb-2'>Sanal Mezun Kartı Bağlantısı</div>
                  <div className='d-flex flex-wrap align-items-center gap-2'>
                    <a
                      href={cardLink}
                      target='_blank'
                      rel='noreferrer'
                      className='text-primary text-decoration-underline'
                    >
                      Kartı Görüntüle
                    </a>
                    <CopyToClipboard text={cardLink} onCopy={() => setCopied(true)}>
                      <button className='btn btn-sm btn-light'>Kopyala</button>
                    </CopyToClipboard>
                    {copied && <span className='text-success small'>Kopyalandı!</span>}
                  </div>
                </div>
              )}
              {isRejected && (
                <div className='alert alert-warning mt-4 mb-0'>
                  <div className='fw-semibold mb-1'>Başvurunuz reddedildi.</div>
                  <div className='small text-muted'>
                    {profile?.rejectionNote
                      ? `Açıklama: ${profile.rejectionNote}`
                      : 'Lütfen bilgilerinizi gözden geçirerek tekrar başvuruda bulunun.'}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export {GraduateCardBox}
